import type { HelpItem } from '@/components/ui';

/** A page-level help topic: an accessible label, a popover heading, and its Q&A entries. */
export interface HelpTopic {
  /** Accessible label for the trigger. */
  label: string;
  /** Heading shown above the Q&A list. */
  title: string;
  /** Q&A entries shown in the popover. */
  items: HelpItem[];
}

/**
 * Per-page help content surfaced by the floating "?" affordance. Keep answers short, formal, and
 * concrete — this is just-in-time guidance, not documentation.
 */
export const HELP_TOPICS = {
  home: {
    label: 'Help with your calendar',
    title: 'Your home calendar',
    items: [
      {
        q: 'What is this view?',
        a: 'Your campaign calendar. Schedule and track shifts, events, and tasks across week, day, and month views.',
      },
      {
        q: 'How do I add something?',
        a: 'Drag a task from the sidebar onto any date or time slot, or use the calendar to create shifts and events.',
      },
      {
        q: 'Whose tasks are shown?',
        a: 'Tasks assigned to you and tasks you created. Inbound and outbound assignments are color-coded.',
      },
    ],
  },
  turf: {
    label: 'Help with field operations',
    title: 'Field operations & turf',
    items: [
      {
        q: 'What can I do here?',
        a: 'Draw and assign canvassing turf on the map, then build walk lists for your canvassers.',
      },
      {
        q: 'Can I cut turf without voter data?',
        a: 'You can preview how turf-cutting works, but cutting a real turf needs a voter file — turf is drawn over your voters. Import or request one from Campaign Settings.',
      },
      {
        q: 'How do I cut turf?',
        a: 'Select Cut Turf and draw a boundary on the map.',
      },
    ],
  },
  staff: {
    label: 'Help with inviting staff',
    title: 'Inviting your team',
    items: [
      {
        q: 'How do invitations work?',
        a: 'Choose a role and enter an email. The person receives a link to set a password and join this campaign. Invitations expire after 7 days.',
      },
      {
        q: 'What does each role grant?',
        a: 'A role is a preset of capabilities across Field Ops, Analytics, Team Management, and Finance. You can fine-tune capabilities per person after they join.',
      },
      {
        q: 'Someone already has an account?',
        a: 'Invite them with the same email and they are added to this campaign immediately — no second sign-up required.',
      },
    ],
  },
  finance: {
    label: 'Help with finance',
    title: 'Finance',
    items: [
      {
        q: 'What lives here?',
        a: 'Your campaign’s contributions, expenditures, and contacts for compliance reporting.',
      },
      {
        q: 'How are records categorized?',
        a: 'Transactions are classified — for example, contribution or expenditure. You can reclassify any record, and changes save immediately.',
      },
      {
        q: 'Where does compliance data come from?',
        a: 'Import existing records, or bring them in from your onboarding checklist.',
      },
    ],
  },
  analytics: {
    label: 'Help with analytics',
    title: 'Campaign performance',
    items: [
      {
        q: 'What does this show?',
        a: 'Canvasser and team performance — contacts made, doors knocked, and outcomes over time.',
      },
      {
        q: 'Why are my numbers low or empty?',
        a: 'Stats build as canvassers log contacts. A new campaign shows little until field work begins.',
      },
      {
        q: 'Can I narrow the data?',
        a: 'Use the filters to scope by canvasser type, team, vendor, or date range.',
      },
    ],
  },
} satisfies Record<string, HelpTopic>;
